import { MovementBlock, MovementBlocker } from "components/Modals/MovementBlocker";
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, TextField } from "@mui/material";
import { useAlert } from "hooks/useAlert";
import { useConfirmation } from "hooks/useConfirmation";
import { FormEvent, useEffect, useRef, useState } from "react";
import { moveInventoryToLocation, scannedLocation } from "./moveInventoryToLocation";

export function SaveAndMoveDialog({
  open,
  inventoryId,
  onClose,
  onMoved,
}: {
  open: boolean;
  inventoryId: number;
  onClose: () => void;
  onMoved: (code: string, locationId: number | null) => Promise<void> | void;
}) {
  const { alertSuccess } = useAlert();
  const { confirm } = useConfirmation();
  const [destination, setDestination] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [moving, setMoving] = useState(false);
  const [blocker, setBlocker] = useState<MovementBlock | null>(null);
  const movingRef = useRef(false);
  const refocusLocation = useRef(false);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!open) return;
    setDestination("");
    setError(null);
    setMoving(false);
  }, [open]);

  useEffect(() => {
    if (!refocusLocation.current || moving) return;
    refocusLocation.current = false;
    inputRef.current?.focus();
    inputRef.current?.select();
  }, [moving, error]);

  const submitDestination = async (code: string, skipActions: Record<string, boolean> = {}) => {
    const typed = code.trim();
    if (!typed || movingRef.current || blocker) return;
    movingRef.current = true;
    setMoving(true);
    setError(null);
    try {
      const result = await moveInventoryToLocation(inventoryId, typed, skipActions);
      if (result.status === "moved") {
        alertSuccess(`Moved item to ${result.code}.`);
        await onMoved(result.code, result.locationId);
        return;
      }
      if (result.status === "skip") {
        confirm({
          message: result.message,
          context: result.param,
          handler: () => {
            void submitDestination(typed, { ...skipActions, [result.param]: true });
          },
        });
        return;
      }
      if (result.status === "refused" && result.blocker) {
        setBlocker(result.blocker);
        return;
      }
      setError(result.message);
      refocusLocation.current = true;
    } finally {
      movingRef.current = false;
      setMoving(false);
    }
  };

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();
    event.stopPropagation();
    void submitDestination(destination);
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={() => {
          if (!moving && !blocker) onClose();
        }}
        disableRestoreFocus
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>Scan destination location</DialogTitle>
        <form onSubmit={handleSubmit}>
          <DialogContent>
            <TextField
              autoFocus
              fullWidth
              autoComplete="off"
              label="Destination location"
              placeholder="Scan a location"
              value={destination}
              inputRef={inputRef}
              disabled={moving}
              error={Boolean(error)}
              helperText={error ?? "Scan the location for this item"}
              onFocus={(event) => event.target.select()}
              onChange={(event) => {
                setDestination(scannedLocation(event.target.value));
                setError(null);
              }}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={onClose} disabled={moving}>
              Cancel
            </Button>
            <Button type="submit" variant="contained" disabled={moving || destination.trim() === ""}>
              Move
            </Button>
          </DialogActions>
        </form>
      </Dialog>
      <MovementBlocker
        block={blocker}
        onClose={() => {
          setBlocker(null);
          setDestination("");
        }}
        onExited={() => inputRef.current?.focus()}
      />
    </>
  );
}
